import React, { useState, useEffect } from "react";
import { EditButton, ShowButton } from "@refinedev/antd";
import { useList } from "@refinedev/core";
import { Table, Input, Space, Tag, DatePicker, Select, Card } from "antd";
import type { ColumnsType } from "antd/es/table";
import { SearchOutlined, PlusOutlined, FileTextOutlined, CheckCircleOutlined, ClockCircleOutlined, StopOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { PageHeader } from "../../../components/ui";
import { IJournalEntry, JournalEntryStatus, TransactionSource, getTransactionSourceLabel, getJournalEntryStatusLabel } from "../../../interfaces/accounting";
import dayjs from "dayjs";

const { RangePicker } = DatePicker;

export const JournalEntriesList: React.FC = () => {
    const [searchText, setSearchText] = useState<string>("");
    const [filteredItems, setFilteredItems] = useState<IJournalEntry[]>([]);
    const [statusFilter, setStatusFilter] = useState<JournalEntryStatus | null>(null);
    const [sourceFilter, setSourceFilter] = useState<TransactionSource | null>(null);
    const [dateRange, setDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs] | null>(null);
    const navigate = useNavigate();

    const { data, isLoading } = useList({
        resource: "journalentries",
    });

    useEffect(() => {
        if (!data?.data) return;

        let filtered = data.data as IJournalEntry[];

        if (searchText.trim() !== "") {
            const searchLower = searchText.toLowerCase();
            filtered = filtered.filter((entry: IJournalEntry) =>
                entry.journalNumber.toLowerCase().includes(searchLower) ||
                entry.description.toLowerCase().includes(searchLower) ||
                entry.reference?.toLowerCase().includes(searchLower)
            );
        }

        if (statusFilter !== null) {
            filtered = filtered.filter((entry) => entry.status === statusFilter);
        }

        if (sourceFilter !== null) {
            filtered = filtered.filter((entry) => entry.source === sourceFilter);
        }

        if (dateRange) {
            const [startDate, endDate] = dateRange;
            filtered = filtered.filter((entry) => {
                const entryDate = dayjs(entry.transactionDate);
                return entryDate.isAfter(startDate.subtract(1, 'day')) && entryDate.isBefore(endDate.add(1, 'day'));
            });
        }

        setFilteredItems(filtered);
    }, [data, searchText, statusFilter, sourceFilter, dateRange]);

    const getStatusIcon = (status: JournalEntryStatus) => {
        switch (status) {
            case JournalEntryStatus.Draft: return <ClockCircleOutlined />;
            case JournalEntryStatus.Posted: return <CheckCircleOutlined />;
            case JournalEntryStatus.Cancelled: return <StopOutlined />;
            default: return null;
        }
    };

    const getStatusColor = (status: JournalEntryStatus): string => {
        switch (status) {
            case JournalEntryStatus.Draft: return 'warning';
            case JournalEntryStatus.Posted: return 'success';
            case JournalEntryStatus.Cancelled: return 'error';
            default: return 'default';
        }
    };

    const columns: ColumnsType<IJournalEntry> = [
        {
            title: "Journal #",
            dataIndex: "journalNumber",
            key: "journalNumber",
            render: (value: string) => (
                <span className="font-medium text-text-primary">{value}</span>
            ),
            sorter: (a, b) => a.journalNumber.localeCompare(b.journalNumber),
        },
        {
            title: "Date",
            dataIndex: "transactionDate",
            key: "transactionDate",
            render: (value: string) => (
                <span className="text-text-secondary text-sm">
                    {dayjs(value).format('DD/MM/YYYY')}
                </span>
            ),
            sorter: (a, b) => dayjs(a.transactionDate).unix() - dayjs(b.transactionDate).unix(),
        },
        {
            title: "Description",
            dataIndex: "description",
            key: "description",
            render: (value: string) => (
                <span className="text-text-secondary">{value}</span>
            ),
            ellipsis: true,
        },
        {
            title: "Source",
            dataIndex: "source",
            key: "source",
            render: (value: TransactionSource) => (
                <Tag className="status-tag">{getTransactionSourceLabel(value)}</Tag>
            ),
            responsive: ['md'],
        },
        {
            title: "Debits",
            dataIndex: "totalDebits",
            key: "totalDebits",
            render: (value: number) => (
                <span className="font-semibold text-primary">${value?.toLocaleString()}</span>
            ),
            align: "right",
        },
        {
            title: "Status",
            dataIndex: "status",
            key: "status",
            render: (value: JournalEntryStatus, record: IJournalEntry) => (
                <Space size="small">
                    <Tag color={getStatusColor(value)} icon={getStatusIcon(value)} className="status-tag">
                        {getJournalEntryStatusLabel(value)}
                    </Tag>
                    {record.isReversed && <Tag color="purple">Reversed</Tag>}
                </Space>
            ),
        },
        {
            title: "Actions",
            key: "actions",
            width: 100,
            render: (_, record) => (
                <Space size="small">
                    <ShowButton hideText size="small" recordItemId={record.id} />
                    {record.status === JournalEntryStatus.Draft && (
                        <EditButton hideText size="small" recordItemId={record.id} />
                    )}
                </Space>
            ),
        },
    ];

    return (
        <div className="page-container">
            <PageHeader
                title="Journal Entries"
                icon={<FileTextOutlined />}
                breadcrumbs={[{ title: "Accounting" }, { title: "Journal Entries" }]}
                actions={[
                    {
                        label: "Create",
                        onClick: () => navigate("/journalentries/create"),
                        type: "primary",
                        icon: <PlusOutlined />,
                    },
                ]}
            />

            <div className="data-table-card">
                <Card>
                    <Space style={{ marginBottom: 16 }} wrap>
                        <Input
                            placeholder="Search by journal #, description, or reference"
                            prefix={<SearchOutlined className="text-text-muted" />}
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                            style={{ width: 300 }}
                            allowClear
                            size="large"
                        />
                        <Select
                            placeholder="Status"
                            value={statusFilter}
                            onChange={(value) => setStatusFilter(value)}
                            style={{ width: 120 }}
                            allowClear
                            size="large"
                        >
                            <Select.Option value={JournalEntryStatus.Draft}>Draft</Select.Option>
                            <Select.Option value={JournalEntryStatus.Posted}>Posted</Select.Option>
                            <Select.Option value={JournalEntryStatus.Cancelled}>Cancelled</Select.Option>
                        </Select>
                        <Select
                            placeholder="Source"
                            value={sourceFilter}
                            onChange={(value) => setSourceFilter(value)}
                            style={{ width: 130 }}
                            allowClear
                            size="large"
                        >
                            <Select.Option value={TransactionSource.Manual}>Manual</Select.Option>
                            <Select.Option value={TransactionSource.Sales}>Sales</Select.Option>
                            <Select.Option value={TransactionSource.Purchase}>Purchase</Select.Option>
                        </Select>
                        <RangePicker
                            value={dateRange}
                            onChange={(dates) => setDateRange(dates as [dayjs.Dayjs, dayjs.Dayjs] | null)}
                            format="DD/MM/YYYY"
                            size="large"
                        />
                    </Space>

                    <Table
                        dataSource={filteredItems}
                        columns={columns}
                        rowKey="id"
                        loading={isLoading}
                        pagination={{
                            pageSize: 15,
                            showSizeChanger: true,
                            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total}`,
                        }}
                        className="custom-table"
                        scroll={{ x: 1000 }}
                    />
                </Card>
            </div>
        </div>
    );
};
